// packages/web/src/lib/backoff.ts

/** Options for {@link nextDelay}. All fields optional; sensible defaults applied. */
export interface BackoffOptions {
  /** Initial delay (ms) at attempt 0. Default 1000. */
  baseMs?: number;
  /** Maximum delay (ms) the exponential is clamped to. Default 30000. */
  capMs?: number;
  /** Exponential growth factor per attempt. Default 2. */
  factor?: number;
  /**
   * Jitter fraction in [0, 1]. 1 = full jitter (delay anywhere in [0, exp]);
   * 0 = no jitter (deterministic exp). Default 1 (full jitter).
   */
  jitter?: number;
  /** Random source in [0, 1). Inject for deterministic tests. Default Math.random. */
  rng?: () => number;
}

/**
 * Capped exponential backoff with (full by default) jitter.
 *
 * Computes `exp = min(capMs, baseMs * factor**attempt)`, then applies jitter:
 * the result is `exp * (1 - jitter) + exp * jitter * rng()`. With the default
 * `jitter = 1` this is AWS-style full jitter: a uniform draw in `[0, exp]`.
 *
 * Negative attempts are clamped to 0. The result never exceeds `capMs`.
 */
export function nextDelay(attempt: number, opts: BackoffOptions = {}): number {
  const baseMs = opts.baseMs ?? 1000;
  const capMs = opts.capMs ?? 30000;
  const factor = opts.factor ?? 2;
  const jitter = opts.jitter ?? 1;
  const rng = opts.rng ?? Math.random;

  const safeAttempt = attempt < 0 ? 0 : attempt;
  const exp = Math.min(capMs, baseMs * factor ** safeAttempt);

  const fixed = exp * (1 - jitter);
  const random = exp * jitter * rng();
  return fixed + random;
}
